import { Controller } from "@hotwired/stimulus"

// Reading a value off a body metric's line.
//
// The chart is drawn server-side as SVG — see body/_chart. This adds the one
// thing markup cannot do on its own: telling you what a point actually was.
// A line with no readable values shows a shape and withholds the data, which
// for a weight chart is the wrong half.
//
// No chart library behind this. The alternative was Chartkick plus Chart.js,
// which is ~200KB and a canvas renderer to draw one line — and a canvas chart
// cannot be styled by the theme, does not render before JS loads, and prints
// as a bitmap. The points are already in the DOM as data attributes.
export default class extends Controller {
  static targets = ["line", "point", "readout", "value", "date", "marker"]

  connect() {
    this.drawLineOn()

    this.points = this.pointTargets.map((element) => ({
      // Where it sits as a PERCENTAGE of the width, for hit-testing against
      // a cursor position measured the same way. `x` below is viewBox units,
      // which is what the marker is positioned with — the two are only equal
      // by coincidence at a 100-unit box, and comparing them made the first
      // point win every hover.
      at: parseFloat(element.dataset.at),
      x: parseFloat(element.dataset.x),
      y: parseFloat(element.dataset.y),
      value: element.dataset.value,
      date: element.dataset.date
    }))
  }

  // THE LINE DRAWS ITSELF ON, left to right.
  //
  // A stroke dashed by its OWN length is one dash and one gap, so offsetting
  // it by that length hides the line entirely; sliding the offset back to
  // zero reveals it from the start. The classic SVG line-drawing trick, and
  // the only way to animate a path without a library.
  //
  // Measured here because only the browser knows a path's length — the
  // server would have to reimplement the geometry to guess it.
  drawLineOn() {
    if (!this.hasLineTarget) return

    try {
      const length = this.lineTarget.getTotalLength()
      if (!length) return

      this.lineTarget.style.setProperty("--trend-length", length)
    } catch {
      // getTotalLength throws on a polyline in some older engines. The line
      // then renders undashed and fully drawn, which is the right fallback:
      // no animation rather than an invisible chart.
    }
  }

  // Nearest point on the x axis, not the nearest in both directions: you are
  // pointing at a moment in time and asking what the reading was, and a
  // vertical distance you did not intend should not change the answer.
  move(event) {
    if (!this.points.length) return

    const box = this.element.getBoundingClientRect()
    const x = ((event.clientX - box.left) / box.width) * 100

    let nearest = this.points[0]
    for (const point of this.points) {
      if (Math.abs(point.at - x) < Math.abs(nearest.at - x)) nearest = point
    }

    this.show(nearest)
  }

  show(point) {
    if (this.hasValueTarget) this.valueTarget.textContent = point.value
    if (this.hasDateTarget) this.dateTarget.textContent = point.date

    if (this.hasMarkerTarget) {
      this.markerTarget.setAttribute("cx", point.x)
      this.markerTarget.setAttribute("cy", point.y)
      this.markerTarget.style.opacity = "1"
    }

    if (this.hasReadoutTarget) {
      this.readoutTarget.style.opacity = "1"
      // Follows the point rather than sitting still, so the number is beside
      // what it describes. Clamped so it cannot run off either end.
      const clamped = Math.min(Math.max(point.at, 12), 88)
      this.readoutTarget.style.left = `${clamped}%`

      // AND FOLLOWS IT VERTICALLY. Pinned at the top of the chart it sat at
      // one height whatever you hovered — fine on a 120-unit box where the
      // whole plot is 100px tall, and plainly detached on a taller one,
      // where the readout floated above a point halfway down.
      //
      // point.y is viewBox units, so it is turned into a percentage of the
      // box's own height the same way `at` is for the horizontal.
      const box = this.element.querySelector("svg")?.viewBox?.baseVal
      if (box?.height) {
        // Floored, because the box is lifted clear of the point by its own
        // height: a reading at the very top of the plot would otherwise put
        // the readout above the chart entirely, where it is clipped by
        // whatever sits above. Below that floor it simply overlaps the top
        // gridline, which is readable.
        const above = Math.max((point.y / box.height) * 100, 22)
        this.readoutTarget.style.top = `${above}%`
      }
    }
  }

  // Back to the latest reading, which is what the card states above — so
  // leaving the chart returns it to agreeing with the headline figure rather
  // than freezing on whatever you happened to point at last.
  leave() {
    if (this.hasReadoutTarget) this.readoutTarget.style.opacity = "0"
    if (this.hasMarkerTarget) this.markerTarget.style.opacity = "0"
  }
}
